import clsx from "clsx";
import {
  BORDER_DIVIDER,
  BORDER_SUBTLE,
  CARD_FOOTER,
  CARD_HEADER,
  CONTEXT_RAIL_WIDTH,
  DIFF_ADDED,
  DIFF_REMOVED,
  GAP_SURFACE,
  PAD_BAR,
  PAD_CARD,
  PAD_INSET,
  RADIUS_INSET,
  RADIUS_SURFACE,
  SPACE_PAGE,
  SURFACE_INSET,
  SURFACE_RAISED,
  TEXT_BODY,
  TEXT_EYEBROW,
  TEXT_MUTED,
  TEXT_ROW_SUBTITLE,
  TEXT_ROW_TITLE,
  TEXT_TITLE,
} from "@/components/ui/surface/surfaceTokens";
import { HOME_SURFACE_RAISED } from "@/components/home/homeSurfaces";

/**
 * Task Review / HITL Task Console surface tokens.
 * Built on shared surfaceTokens; use these in review/ and context-panels/.
 */

export const REVIEW_WORKSPACE_SHELL = clsx(
  "flex h-full min-h-0 flex-col",
  SPACE_PAGE,
  GAP_SURFACE,
  "bg-slate-100 dark:bg-transparent",
);

/** Raised, rounded shell cell — header, tabs, rail, and main panel share one bento width. */
export const REVIEW_BENTO_CELL = clsx("w-full min-w-0", RADIUS_SURFACE, HOME_SURFACE_RAISED);

/** Side rail in the review split — fixed width; must not use `w-full` (crushes the main panel when collapsed). */
export const REVIEW_BENTO_RAIL = clsx("min-w-0 shrink-0", RADIUS_SURFACE, HOME_SURFACE_RAISED);

export const REVIEW_RUN_HEADER = clsx("shrink-0", REVIEW_BENTO_CELL, PAD_BAR);

export const REVIEW_TABS_BAR = clsx(
  "shrink-0",
  REVIEW_BENTO_CELL,
  "px-2 pt-1 pb-2 sm:px-3 sm:pb-3",
);

export const REVIEW_RUN_HEADER_TITLE = TEXT_EYEBROW;

export const REVIEW_RUN_HEADER_HEADLINE = clsx(
  "text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg",
);

export const REVIEW_MAIN_PANEL = clsx(
  "flex min-h-0 flex-1 flex-col overflow-hidden",
  REVIEW_BENTO_CELL,
);

export const REVIEW_CONTEXT_RAIL = clsx(
  "flex w-full shrink-0 flex-col border-l",
  BORDER_DIVIDER,
  REVIEW_BENTO_CELL,
  CONTEXT_RAIL_WIDTH,
);

/** Card footer inside `REVIEW_MAIN_PANEL` — border only; padding lives on `ReviewActionBar`. */
export const REVIEW_ACTION_BAR = clsx("shrink-0 border-t", BORDER_DIVIDER);

/** Equal inset on all sides — matches `REVIEW_PAD_CARD`, with `!` to override `CardFooter` defaults. */
export const REVIEW_ACTION_BAR_PAD = "!p-4 sm:!p-5";

/** Outer gutter for run-plan rail step lists — keeps hover rows off the rail edge. */
export const REVIEW_RAIL_INSET_X = "px-4 sm:px-5";

/** Inner padding on rail step rows — space between icon and hover background edge. */
export const REVIEW_RAIL_ROW_PAD = "px-2.5 sm:px-3";

export const REVIEW_CONTEXT_PANEL_SHELL = clsx(
  "flex min-h-0 flex-1 flex-col overflow-hidden",
  PAD_CARD,
);

export const REVIEW_CONTEXT_PANEL_TITLE = TEXT_TITLE;

export const REVIEW_CONTEXT_PANEL_BODY = TEXT_BODY;

export const REVIEW_CONTEXT_PANEL_MUTED = TEXT_MUTED;

export const REVIEW_CONTEXT_INSET = clsx(RADIUS_INSET, PAD_INSET, SURFACE_INSET);

export const REVIEW_CARD = clsx(RADIUS_INSET, PAD_CARD, SURFACE_RAISED);

/** Centred main-column body — matches wireframe max width and inset. */
export const REVIEW_MAIN_CONTENT = clsx(
  "mx-auto w-full max-w-3xl space-y-5",
  PAD_CARD,
);

/** Full-width main column for embedded tools (calculators, wide tables). */
export const REVIEW_MAIN_CONTENT_WIDE = clsx(
  "mx-auto w-full max-w-none space-y-5",
  PAD_CARD,
);

/** Raised list shell with row dividers (evidence, checks). */
export const REVIEW_SECTION_LIST = clsx(
  RADIUS_INSET,
  SURFACE_RAISED,
  "divide-y overflow-hidden",
  BORDER_DIVIDER,
);

export const REVIEW_TIMELINE_RAIL = clsx(
  "flex flex-col gap-0 overflow-y-auto pl-3 pr-1",
);

export const REVIEW_TIMELINE_ITEM = clsx(
  "relative border-l-2 pl-5 pb-5 last:pb-0",
  "border-slate-300/80 dark:border-slate-600/80",
);

export const REVIEW_TIMELINE_DOT = clsx(
  "absolute left-[-1px] top-1.5 size-2 shrink-0 -translate-x-1/2 rounded-full",
  "bg-indigo-500 dark:bg-indigo-400",
);

export const REVIEW_TIMELINE_TIMESTAMP = TEXT_ROW_SUBTITLE;

export const REVIEW_TIMELINE_TITLE = TEXT_ROW_TITLE;

export const REVIEW_TIMELINE_DETAIL = TEXT_BODY;

export const REVIEW_DIFF_REMOVED = DIFF_REMOVED;

export const REVIEW_DIFF_ADDED = DIFF_ADDED;

export const REVIEW_DECISION_RECORD = clsx(
  RADIUS_INSET,
  "border p-4",
  BORDER_SUBTLE,
  SURFACE_INSET,
);

export const REVIEW_DECISION_LABEL = TEXT_EYEBROW;

export const REVIEW_DECISION_VALUE = TEXT_ROW_TITLE;

export const REVIEW_CONSOLE_AUDIT_GRID = clsx(
  "grid min-h-0 flex-1 overflow-hidden lg:grid-cols-2",
  GAP_SURFACE,
);

export const REVIEW_CONSOLE_AUDIT_COLUMN = clsx(
  "flex min-h-0 min-w-0 flex-col overflow-hidden border",
  BORDER_SUBTLE,
  RADIUS_INSET,
  HOME_SURFACE_RAISED,
);

export const REVIEW_CONSOLE_SECTION_TITLE = clsx(
  "shrink-0 border-b text-sm font-semibold",
  PAD_BAR,
  BORDER_DIVIDER,
  TEXT_ROW_TITLE,
);

/** Scrollable panel body — use in columns that fill available height (e.g. audit timeline). */
export const REVIEW_CONSOLE_SECTION_BODY = clsx(
  "min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto",
  PAD_CARD,
);

/** Natural-height panel body — use when stacked sections must show full content with padding. */
export const REVIEW_CONSOLE_SECTION_BODY_STATIC = clsx(
  "min-w-0 overflow-x-hidden pb-5",
  PAD_CARD,
);

// ── Layout aliases ──────────────────────────────────────────────────────────

export const REVIEW_SPACE_PAGE = SPACE_PAGE;

export const REVIEW_GAP_SURFACE = GAP_SURFACE;

export const REVIEW_PAD_CARD = PAD_CARD;

export const REVIEW_PAD_BAR = PAD_BAR;

export const REVIEW_CARD_HEADER = CARD_HEADER;

export const REVIEW_CARD_FOOTER = CARD_FOOTER;

export const REVIEW_EYEBROW_CLASS = TEXT_EYEBROW;
